/**
 * XAI Helpers — Build summary layer and evidence from prompt_data
 *
 * Parses the system_prompt text to identify which user context (UC) sections
 * were injected, extracts key factors, and computes feature importance.
 *
 * Patent claims exercised: P-003 Claims 4-5 (explainable AI transparency layers)
 */

// ── Factor extraction patterns ──
const UC_PATTERNS = [
  { pattern: /ALLERGEN|allergies|allerg/i, label: 'Allergen Safety', icon: '\u26A0\uFE0F', importance: 100, shortValue: 'Allergen restrictions enforced' },
  { pattern: /medication|drug.?food|prescription|Lisinopril|Atorvastatin|ACE.?inhibitor|statin|SSRI|metformin/i, label: 'Medication Safety', icon: '\uD83D\uDC8A', importance: 85, shortValue: 'Drug-food interactions checked' },
  { pattern: /MEDICAL PROFILE|medical.?condition|conditions?:/i, label: 'Medical Conditions', icon: '\uD83C\uDFE5', importance: 82, shortValue: 'Health conditions considered' },
  { pattern: /HEALTH OBJECTIVES|health.?objective|goal/i, label: 'Health Objectives', icon: '\u2764\uFE0F', importance: 78, shortValue: 'Goals aligned' },
  { pattern: /provider.?rec|Dr\.|physician|cardiolog|endocrinolog/i, label: 'Provider Recommendations', icon: '\uD83D\uDC68\u200D\u2695\uFE0F', importance: 72, shortValue: 'Provider guidance applied' },
  { pattern: /trainer.?rec|personal.?trainer|coach/i, label: 'Trainer Recommendations', icon: '\uD83C\uDFCB\uFE0F', importance: 68, shortValue: 'Trainer input applied' },
  { pattern: /BIOMETRIC|BMI|weight|height|waist|body.?characteristic/i, label: 'Body Profile', icon: '\uD83D\uDCCA', importance: 65, shortValue: 'Body metrics used' },
  { pattern: /activity.?level|VO2|cardio.?level|exercise.?level|fitness.?level/i, label: 'Activity Level', icon: '\uD83C\uDFC3', importance: 60, shortValue: 'Activity factored in' },
  { pattern: /food.?preference|cuisine|vegetarian|vegan|kosher|halal/i, label: 'Food Preferences', icon: '\uD83C\uDF7D\uFE0F', importance: 55, shortValue: 'Preferences respected' },
  { pattern: /diet.?plan|dietary.?restriction|DASH|Mediterranean|keto/i, label: 'Diet Plan', icon: '\uD83E\uDD57', importance: 55, shortValue: 'Diet plan aligned' },
  { pattern: /pantry|inventory|available.?ingredient/i, label: 'Pantry Inventory', icon: '\uD83D\uDED2', importance: 45, shortValue: 'Pantry items considered' },
  { pattern: /recipe|saved.?recipe/i, label: 'Saved Recipes', icon: '\uD83D\uDCD6', importance: 35, shortValue: 'Recipe library referenced' },
  { pattern: /device.?data|heart.?rate|sleep|SpO2|wearable|Oura/i, label: 'Device Data', icon: '\u231A', importance: 50, shortValue: 'Wearable data integrated' },
  { pattern: /sports|hobbies/i, label: 'Sports & Hobbies', icon: '\u26BD', importance: 40, shortValue: 'Activities considered' },
  { pattern: /family|parental|hereditary/i, label: 'Family History', icon: '\uD83D\uDC68\u200D\uD83D\uDC69\u200D\uD83D\uDC67', importance: 50, shortValue: 'Family history noted' },
  { pattern: /fitness.?program|exercise.?plan|weight.?training/i, label: 'Active Programs', icon: '\uD83D\uDCAA', importance: 45, shortValue: 'Programs integrated' },
  { pattern: /medical.?record|lab.?value|CBC|lipid|A1c/i, label: 'Medical Records', icon: '\uD83D\uDCC4', importance: 70, shortValue: 'Lab values reviewed' },
];

// ── Evidence templates by topic ──
//
// Apple App Review Guideline 1.4.1 (2026-10-01 rejection): "Include citations in the app of the
// sources of the recommendations or information, SUCH AS LINKS TO THOSE SOURCES." Every entry
// therefore carries a resolvable `url` — DOIs via doi.org (self-validating), otherwise the
// publishing body's canonical page.
const EVIDENCE_TEMPLATES = [
  {
    pattern: /allergen|allerg/i,
    title: 'Food Allergen Safety',
    description: 'The FDA identifies 9 major food allergens (milk, eggs, fish, shellfish, tree nuts, peanuts, wheat, soybeans, sesame) responsible for most serious allergic reactions. Cross-contamination must also be considered.',
    source: 'FDA Food Allergen Labeling and Consumer Protection Act (FALCPA). 21 U.S.C. 343(w).',
    url: 'https://www.law.cornell.edu/uscode/text/21/343'
  },
  {
    pattern: /drug.?food|medication|ACE.?inhibitor|potassium|Lisinopril/i,
    title: 'Drug-Food Interactions',
    description: 'Certain foods can significantly alter drug metabolism, absorption, or effect. ACE inhibitors and potassium-rich foods, statins and grapefruit, warfarin and vitamin K foods are well-documented interactions requiring dietary awareness.',
    source: 'Bushra R, et al. "Food-Drug Interactions." Oman Med J. 2011;26(2):77-83. doi:10.5001/omj.2011.21',
    url: 'https://doi.org/10.5001/omj.2011.21'
  },
  {
    pattern: /grapefruit|CYP3A4|statin|atorvastatin/i,
    title: 'Statin-Grapefruit CYP3A4 Inhibition',
    description: 'Furanocoumarins in grapefruit irreversibly inhibit intestinal CYP3A4, increasing atorvastatin bioavailability by up to 250%, elevating risk of myopathy.',
    source: 'Bailey DG, et al. CMAJ. 2013;185(4):309-316. doi:10.1503/cmaj.120951',
    url: 'https://doi.org/10.1503/cmaj.120951'
  },
  {
    pattern: /Mediterranean|DASH|cardiovascular|heart/i,
    title: 'Mediterranean/DASH Diet for Cardiovascular Health',
    description: 'The PREDIMED study demonstrated 30% reduction in cardiovascular events with Mediterranean diet. DASH diet shown to reduce blood pressure by 5-11 mmHg.',
    source: 'Estruch R, et al. N Engl J Med. 2018;378(25):e34. Sacks FM, et al. N Engl J Med. 2001;344(1):3-10.',
    url: 'https://www.nhlbi.nih.gov/health/dash-eating-plan'
  },
  {
    pattern: /omega.?3|fish.?oil|EPA|DHA|salmon/i,
    title: 'Omega-3 Fatty Acids',
    description: 'EPA+DHA at 2-4g/day reduces triglycerides by 25-30%. AHA recommends fatty fish 2+ servings/week for cardiovascular benefit.',
    source: 'AHA/ACC Guideline. Grundy SM, et al. J Am Coll Cardiol. 2019;73(24):e285-e350.',
    url: 'https://ods.od.nih.gov/factsheets/Omega3FattyAcids-HealthProfessional/'
  },
  {
    pattern: /BMI|weight.?loss|calorie|caloric.?deficit|obesity/i,
    title: 'Evidence-Based Weight Management',
    description: 'A sustained caloric deficit of 500-750 kcal/day produces clinically significant weight loss of 0.5-0.75 kg/week. Protein intake of 1.2-1.6g/kg preserves lean mass during deficit.',
    source: 'Jensen MD, et al. AHA/ACC/TOS Guideline. J Am Coll Cardiol. 2014;63(25):2985-3023.',
    url: 'https://www.nhlbi.nih.gov/health-topics/managing-overweight-obesity-in-adults'
  },
  {
    pattern: /strength|resistance|weight.?training|muscle|hypertrophy/i,
    title: 'Resistance Training Guidelines',
    description: 'ACSM recommends 2-3 days/week of resistance training for all major muscle groups. 8-12 repetitions at 60-80% 1RM for hypertrophy, with progressive overload.',
    source: 'ACSM Position Stand. Med Sci Sports Exerc. 2009;41(3):687-708. doi:10.1249/MSS.0b013e3181915670',
    url: 'https://doi.org/10.1249/MSS.0b013e3181915670'
  },
  {
    pattern: /exercise|fitness|cardio|aerobic|VO2/i,
    title: 'Aerobic Exercise Recommendations',
    description: 'WHO recommends 150-300 min/week moderate-intensity or 75-150 min/week vigorous-intensity aerobic activity. Additional health benefits from exceeding 300 min/week.',
    source: 'WHO Guidelines on Physical Activity and Sedentary Behaviour. 2020. ISBN: 978-92-4-001512-8.',
    url: 'https://www.who.int/publications/i/item/9789240015128'
  },
  {
    pattern: /diabetes|blood.?glucose|A1c|insulin|metformin/i,
    title: 'Diabetes Dietary Management',
    description: 'ADA recommends individualized medical nutrition therapy. Focus on carbohydrate quality over quantity, adequate fiber (14g/1000 kcal), limited added sugars.',
    source: 'ADA Standards of Medical Care. Diabetes Care. 2024;47(Suppl 1):S68-S76.',
    url: 'https://diabetesjournals.org/care/issue/47/Supplement_1'
  },
  {
    pattern: /sleep|recovery|HRV|rest/i,
    title: 'Sleep and Recovery',
    description: 'AASM recommends 7+ hours sleep for adults. Poor sleep associated with impaired glucose metabolism, increased appetite hormones, and reduced exercise recovery.',
    source: 'Watson NF, et al. J Clin Sleep Med. 2015;11(6):591-592. doi:10.5664/jcsm.4758',
    url: 'https://doi.org/10.5664/jcsm.4758'
  },
];

// ── Baseline citations: the standards a generation rests on REGARDLESS of the user ──
//
// 🔴 THE ROOT CAUSE OF THE 1.4.1 REJECTION. The templates above all match against the USER's
// health context, so an account with an empty profile matched NOTHING and the Citations tab
// rendered "No specific clinical evidence citations available for this generation."
// Measured on healist_dev 2026-10-01: users WITH health data saw 7-10 citations; every
// empty-profile account saw 0-2. An App Store reviewer works from exactly such an account.
//
// Apple does not ask "what do you know about this user" — it asks where the information the app
// just produced comes from. A meal plan rests on the Dietary Guidelines and the DRIs whether or
// not the user has diabetes. These are keyed to the OUTPUT, so they are always present.
//
// `verifyTerms` (G16, 2026-10-18) is NOT shown to the user. The weekly citation check on the Prod
// scheduler (backend/app/services/citation_monitor.py) parses this table and confirms each page
// still loads AND still contains every term; a moved, dead or changed page becomes an event on
// the Admin Portal "Scheduled Jobs" tab. Terms were confirmed on each page on 2026-10-18. A DOI
// link is checked through the DOI handle system instead, so it carries no terms.
const BASELINE_CITATIONS = {
  nutrition: [
    {
      title: 'Dietary Guidelines for Americans',
      description: 'Nutrition targets, meal composition and food-group balance follow the current Dietary Guidelines for Americans, issued jointly by USDA and HHS.',
      source: 'U.S. Departments of Agriculture and Health and Human Services. Dietary Guidelines for Americans.',
      url: 'https://www.dietaryguidelines.gov/',
      verifyTerms: ['dietary guidelines'],
    },
    {
      title: 'Dietary Reference Intakes (DRI)',
      description: 'Macronutrient and micronutrient targets are bounded by the Dietary Reference Intakes established by the National Academies of Sciences, Engineering, and Medicine.',
      source: 'National Academies of Sciences, Engineering, and Medicine. Dietary Reference Intakes.',
      url: 'https://ods.od.nih.gov/HealthInformation/nutrientrecommendations.aspx',
      verifyTerms: ['dietary reference intakes'],
    },
  ],
  fitness: [
    {
      title: 'ACSM Exercise Prescription Guidelines',
      description: 'Training volume, intensity, frequency and progression follow the American College of Sports Medicine guidelines for exercise testing and prescription.',
      source: 'ACSM Position Stand. Med Sci Sports Exerc. 2009;41(3):687-708. doi:10.1249/MSS.0b013e3181915670',
      url: 'https://doi.org/10.1249/MSS.0b013e3181915670'
    },
    {
      title: 'WHO Physical Activity Guidelines',
      description: 'Weekly activity targets follow the WHO Guidelines on Physical Activity and Sedentary Behaviour (2020).',
      source: 'WHO Guidelines on Physical Activity and Sedentary Behaviour. 2020. ISBN: 978-92-4-001512-8.',
      url: 'https://www.who.int/publications/i/item/9789240015128',
      verifyTerms: ['physical activity', 'sedentary behaviour'],
    },
  ],
  supplements: [
    {
      title: 'NIH Office of Dietary Supplements',
      description: 'Supplement information, upper intake levels and nutrient interactions are drawn from the NIH Office of Dietary Supplements fact sheets for health professionals.',
      source: 'National Institutes of Health, Office of Dietary Supplements.',
      url: 'https://ods.od.nih.gov/factsheets/list-all/',
      verifyTerms: ['fact sheets', 'health professional'],
    },
  ],
  // Longevity Intelligence (G16, approved by Vincent 2026-10-18). The standards every longevity
  // option is framed against, shown alongside each card's own evidence and warnings.
  longevity: [
    {
      title: 'Physical Activity Guidelines for Americans',
      description: 'Exercise options (aerobic, interval and strength training) are framed against the U.S. Physical Activity Guidelines: 150–300 minutes of moderate aerobic activity a week, plus muscle strengthening at least 2 days a week.',
      source: 'U.S. Department of Health and Human Services. Physical Activity Guidelines for Americans, 2nd edition.',
      url: 'https://odphp.health.gov/our-work/nutrition-physical-activity/physical-activity-guidelines/current-guidelines',
      verifyTerms: ['physical activity guidelines'],
    },
    {
      title: 'NIH — How Much Sleep Is Enough?',
      description: 'Sleep guidance follows the NIH National Heart, Lung, and Blood Institute: adults need 7 to 9 hours a night.',
      source: 'NIH National Heart, Lung, and Blood Institute. How Much Sleep Is Enough?',
      url: 'https://www.nhlbi.nih.gov/health/sleep/how-much-sleep',
      verifyTerms: ['7 and 9 hours'],
    },
    {
      title: 'FDA — Aesthetic (Cosmetic) Devices',
      description: 'Aesthetic procedures are framed against the FDA\'s information on cleared and approved aesthetic devices, their intended uses and their risks.',
      source: 'U.S. Food and Drug Administration. Aesthetic (Cosmetic) Devices.',
      url: 'https://www.fda.gov/medical-devices/products-and-medical-procedures/aesthetic-cosmetic-devices',
      verifyTerms: ['aesthetic', 'devices'],
    },
  ],
  // Drug-nutrient TIMING surfaces (PK scheduling, interaction warnings). These previously fell
  // through to `general` and cited the Dietary Guidelines + WHO physical activity — accurate
  // documents, but irrelevant to a medication-timing explanation, which reads as citation
  // theatre rather than evidence. Both URLs verified reachable 2026-10-03 (200 on the real path,
  // 404 on a deliberately bogus path on the same host).
  medication: [
    {
      title: 'FDA — Drug Interactions: What You Should Know',
      description: 'Drug-food and drug-nutrient interaction guidance follows the U.S. Food and Drug Administration\'s consumer and prescriber information on drug interactions.',
      source: 'U.S. Food and Drug Administration. Drug Interactions: What You Should Know.',
      // Moved by FDA (from /resources-you-drugs/): found by the G16 baseline probe 2026-10-18.
      url: 'https://www.fda.gov/drugs/resources-drugs/drug-interactions-what-you-should-know',
      verifyTerms: ['drug interactions'],
    },
    {
      title: 'NIH MedlinePlus Drug Information',
      description: 'Medication properties, administration timing and food considerations are drawn from the NIH National Library of Medicine\'s MedlinePlus drug monographs.',
      source: 'U.S. National Library of Medicine. MedlinePlus Drug Information.',
      url: 'https://medlineplus.gov/druginformation.html',
      verifyTerms: ['drugs', 'medlineplus'],
    },
  ],
  // --- Added 2026-10-17 (Apple 1.4.1, third round) for the non-AI checks now carrying citations.
  //     URLs fetched 2026-10-17: FALCPA (already verified by verify_citation_urls.py), FoodData
  //     Central 200 + "FoodData Central", WHO obesity fact sheet 200 + "BMI", "25", "30".
  allergen: [
    {
      title: 'Food Allergen Safety (FALCPA)',
      description: 'Allergen checks follow the FDA major food allergens defined by the Food Allergen Labeling and Consumer Protection Act (milk, eggs, fish, shellfish, tree nuts, peanuts, wheat, soybeans, sesame).',
      source: 'FDA Food Allergen Labeling and Consumer Protection Act (FALCPA). 21 U.S.C. 343(w).',
      url: 'https://www.law.cornell.edu/uscode/text/21/343',
      verifyTerms: ['major food allergen'],
    },
  ],
  food_composition: [
    {
      title: 'USDA FoodData Central',
      description: 'Nutrient values for foods are estimated against the USDA FoodData Central food composition database.',
      source: 'U.S. Department of Agriculture, Agricultural Research Service. FoodData Central.',
      url: 'https://fdc.nal.usda.gov/',
      verifyTerms: ['fooddata central'],
    },
  ],
  bmi: [
    {
      title: 'WHO Adult BMI Classification',
      description: 'BMI is weight (kg) divided by height (m) squared. Categories follow the World Health Organization adult classification: overweight is a BMI of 25 or more; obesity is 30 or more.',
      source: 'World Health Organization. Obesity and overweight (fact sheet).',
      url: 'https://www.who.int/news-room/fact-sheets/detail/obesity-and-overweight',
      verifyTerms: ['bmi', '25', '30'],
    },
  ],
  general: [
    {
      title: 'Dietary Guidelines for Americans',
      description: 'General nutrition guidance follows the current Dietary Guidelines for Americans, issued jointly by USDA and HHS.',
      source: 'U.S. Departments of Agriculture and Health and Human Services. Dietary Guidelines for Americans.',
      url: 'https://www.dietaryguidelines.gov/',
      verifyTerms: ['dietary guidelines'],
    },
    {
      title: 'WHO Physical Activity Guidelines',
      description: 'General activity guidance follows the WHO Guidelines on Physical Activity and Sedentary Behaviour (2020).',
      source: 'WHO Guidelines on Physical Activity and Sedentary Behaviour. 2020. ISBN: 978-92-4-001512-8.',
      url: 'https://www.who.int/publications/i/item/9789240015128',
      verifyTerms: ['physical activity', 'sedentary behaviour'],
    },
  ],
};

// featureName (passed by every XaiPromptPanel call site) -> which baseline set applies.
// Anything unmatched falls through to `general`, so NO generation can ever render zero citations.
const FEATURE_BASELINES = [
  // ORDER MATTERS — this list is matched with .find(), first hit wins. Medication must precede
  // the twin/schedule catch-all below, or "Medication Schedule Optimization" matches `schedule`
  // and cites dietary + physical-activity guidance on a drug-timing explanation.
  // Family/allergen checks cover allergens AND each member's medications, so they cite both.
  { pattern: /allergen|family/i, keys: ['allergen', 'medication'] },
  { pattern: /\bbmi\b|body mass/i, keys: ['bmi'] },
  { pattern: /photo|food composition|meal analysis/i, keys: ['food_composition', 'nutrition'] },
  { pattern: /diet compliance/i, keys: ['allergen', 'nutrition', 'medication'] },
  { pattern: /medication|drug|prescription|pharmac|interaction|dos(e|ing)|schedule.*(med|drug)|med.*schedule/i, keys: ['medication'] },
  { pattern: /meal.?plan|recipe|nutrition|food|pantry|shopping/i, keys: ['nutrition'] },
  { pattern: /fitness|exercise|workout|training|program/i, keys: ['fitness'] },
  // Supplements are recommended ALONGSIDE the user's prescriptions and the product's value
  // is interaction-awareness, so these cite nutrient sources AND drug-interaction sources.
  { pattern: /supplement/i, keys: ['supplements', 'medication'] },
  // Longevity options span exercise, sleep, supplements, prescription and aesthetic procedures,
  // so its standards are the activity + sleep + aesthetic-device set, the supplement facts and
  // the drug-interaction references (G16, approved by Vincent 2026-10-18).
  { pattern: /longevity/i, keys: ['longevity', 'supplements', 'medication'] },
  { pattern: /score|trend|analytic|twin|schedule|health.?advisor|chat/i, keys: ['general'] },
];

/**
 * Per-result citations from the backend ({key, label, publisher, url} — services/citation_resolver)
 * into the panel's display shape ({title, description, source, url}). Defensive by design (Apple
 * 1.4.1; Vincent 2026-10-17: citation errors handled elegantly): an entry without a label is
 * skipped and logged, a link is only kept when it is an https URL, and duplicates collapse. Never
 * throws — one bad citation must not blank the rest of the panel.
 */
export function normalizeItemCitations(list) {
  if (!Array.isArray(list)) return [];
  const out = [];
  const seen = new Set();
  for (const c of list) {
    if (!c || typeof c !== 'object') continue;
    const title = c.label || c.title;
    if (!title || typeof title !== 'string') {
      // eslint-disable-next-line no-console
      console.error('[citations] skipped a citation with no label', c);
      continue;
    }
    const url = typeof c.url === 'string' && /^https:\/\//i.test(c.url) ? c.url : null;
    const k = c.key || url || title;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({
      title,
      description: c.description || '',
      source: c.publisher || c.source || (url ? 'View source' : ''),
      url,
      // Funding / conflict-of-interest disclosure for an industry-tied source (G16, 2026-10-18).
      note: typeof c.note === 'string' ? c.note : '',
    });
  }
  return out;
}

/**
 * Authority warnings for one result ({text, source: <backend citation>}) into the panel's shape
 * ({text, citation}). Same defensive rules as normalizeItemCitations: a warning without text is
 * skipped and logged; a warning whose source failed to resolve still shows its text (an unlinked
 * warning is better than a hidden one). Never throws. G16 — Vincent 2026-10-18: "recommendations
 * should come with both citations and warnings".
 */
export function normalizeItemWarnings(list) {
  if (!Array.isArray(list)) return [];
  const out = [];
  for (const w of list) {
    if (!w || typeof w !== 'object' || typeof w.text !== 'string' || !w.text.trim()) {
      // eslint-disable-next-line no-console
      console.error('[citations] skipped a warning with no text', w);
      continue;
    }
    const [citation] = normalizeItemCitations(w.source ? [w.source] : []);
    out.push({ text: w.text, citation: citation || null });
  }
  return out;
}

/** How many citations a result shows (its own + the feature's standards) — for button counts. */
export function citationCount(promptData, featureName, itemCitations) {
  return buildXaiEvidence(promptData, featureName).length + normalizeItemCitations(itemCitations).length;
}

export function baselineCitationsFor(featureName = '') {
  const hit = FEATURE_BASELINES.find(b => b.pattern.test(featureName || ''));
  const keys = hit ? hit.keys : ['general'];
  const out = [];
  for (const k of keys) {
    for (const c of (BASELINE_CITATIONS[k] || [])) out.push(c);
  }
  return out;
}

/**
 * Build the Summary layer content from prompt_data.
 * Scans the system_prompt for UC section patterns and builds
 * feature importance rankings (P-003 Claim 5).
 */
export function buildXaiSummary(promptData) {
  if (!promptData) return { factors: [], contextSourceCount: 0, contextSources: [], confidenceLabel: 'Unknown', domainValidation: null };

  // Extract domain validation pre-step if present
  const domainValidation = promptData.domain_validation || null;

  // #101: user_health_context MUST be scanned. Surfaces that keep the UHC OUT of the system prompt
  // for Bedrock prompt caching (#57) — the AI Health Advisor does, passing system_prompt and
  // user_health_context to bedrock as distinct arguments — carry every clinical fact in that second
  // field. Scanning only system_prompt + user_message yielded 0 factors, hence "0 data sources /
  // Low confidence" and statin-not-amlodipine evidence on clinically-grounded answers.
  const fullText = [
    promptData.system_prompt || '',
    promptData.user_health_context || '',
    promptData.user_message || '',
  ].join('\n');

  // Find matching factors
  const factors = [];
  for (const pat of UC_PATTERNS) {
    if (pat.pattern.test(fullText)) {
      // Extract specific detail from the prompt if possible
      let value = pat.shortValue;

      // #101: extract the VALUE off the UHC's own labelled lines, which look like
      //   "MEDICAL CONDITIONS: Celiac disease, CKD stage 3b"        (uhc_prompt.py:155)
      //   "MEDICATIONS (consider drug-food/...): Amlodipine"        (uhc_prompt.py:160)
      //   "STRICT ALLERGIES — NEVER RECOMMEND ...: peanuts"         (uhc_prompt.py:176)
      //   "HEALTH OBJECTIVES: lower blood pressure"                 (uhc_prompt.py:213)
      // The previous patterns matched the bare WORD anywhere in prose, so they latched onto
      // instruction text and rendered fragments like
      //   "Allergen restrictions: , and NEVER recommend anything listed under ABSOLUTE ..."
      //   "Goals: . Anything listed under MANDATORY CAUTIONS ... even when the it"
      // Anchor on the label, require a colon, stop at end of line, and only override the generic
      // shortValue when the captured text is actually substantive.
      const extract = (re) => {
        const m = fullText.match(re);
        if (!m) return null;
        const v = (m[1] || '').trim().replace(/\s+/g, ' ');
        // Reject empties and leading punctuation — the tell of a fragment grab.
        if (v.length < 2 || /^[.,;:—-]/.test(v)) return null;
        return v.length > 120 ? v.substring(0, 117).trimEnd() + '…' : v;
      };

      if (pat.label === 'Allergen Safety') {
        const v = extract(/^[^\S\n]*(?:STRICT\s+)?ALLERG(?:IES|ENS?)\b[^:\n]*:[^\S\n]*([^\n]+)/im);
        if (v) value = `Allergen restrictions: ${v}`;
      }
      if (pat.label === 'Medication Safety') {
        const v = extract(/^[^\S\n]*(?:MEDICATIONS?|PRESCRIPTIONS?)\b[^:\n]*:[^\S\n]*([^\n]+)/im);
        if (v) value = `Medications: ${v}`;
      }
      if (pat.label === 'Medical Conditions') {
        const v = extract(/^[^\S\n]*(?:MEDICAL\s+)?CONDITIONS?\b[^:\n]*:[^\S\n]*([^\n]+)/im);
        if (v) value = `Conditions: ${v}`;
      }
      if (pat.label === 'Health Objectives') {
        const v = extract(/^[^\S\n]*HEALTH\s+OBJECTIVES?\b[^:\n]*:[^\S\n]*([^\n]+)/im);
        if (v) value = `Goals: ${v}`;
      }
      if (pat.label === 'Medical Records') {
        const v = extract(/^[^\S\n]*(?:LAB(?:ORATORY)?\s+(?:RESULTS?|VALUES?)|RECENT\s+LABS?)\b[^:\n]*:[^\S\n]*([^\n]+)/im);
        if (v) value = `Lab values: ${v}`;
      }

      factors.push({
        icon: pat.icon,
        label: pat.label,
        value,
        shortValue: pat.shortValue,
        importance: pat.importance,
      });
    }
  }

  // Sort by importance descending
  factors.sort((a, b) => b.importance - a.importance);

  // Context source count and confidence
  const contextSourceCount = factors.length;
  const contextSources = factors.map(f => f.label);

  let confidenceLabel = 'Low confidence';
  if (contextSourceCount >= 10) confidenceLabel = 'High confidence';
  else if (contextSourceCount >= 6) confidenceLabel = 'Good confidence';
  else if (contextSourceCount >= 3) confidenceLabel = 'Moderate confidence';

  return { factors, contextSourceCount, contextSources, confidenceLabel, domainValidation };
}

/**
 * Build the Evidence layer content from prompt_data.
 * Matches known clinical topics in the prompt against
 * curated evidence templates (P-003 Claim 14).
 */
export function buildXaiEvidence(promptData, featureName = '') {
  // Baselines FIRST: these describe the standards this generation rests on and are present for
  // every output, so the "no citations available" state is now unreachable (Apple 1.4.1).
  const baseline = baselineCitationsFor(featureName);
  if (!promptData) return baseline;

  // #101: user_health_context MUST be scanned. Surfaces that keep the UHC OUT of the system prompt
  // for Bedrock prompt caching (#57) — the AI Health Advisor does, passing system_prompt and
  // user_health_context to bedrock as distinct arguments — carry every clinical fact in that second
  // field. Scanning only system_prompt + user_message yielded 0 factors, hence "0 data sources /
  // Low confidence" and statin-not-amlodipine evidence on clinically-grounded answers.
  const fullText = [
    promptData.system_prompt || '',
    promptData.user_health_context || '',
    promptData.user_message || '',
  ].join('\n');
  const matched = [];

  for (const tmpl of EVIDENCE_TEMPLATES) {
    if (tmpl.pattern.test(fullText)) {
      matched.push({
        title: tmpl.title,
        description: tmpl.description,
        source: tmpl.source,
        url: tmpl.url,
      });
    }
  }

  // Baselines first (what the generation rests on), then the user-specific matches layered on
  // top. De-duplicated by title so a standard that is both a baseline AND a keyword match —
  // WHO physical activity on a fitness plan, for instance — appears exactly once.
  const seen = new Set();
  const out = [];
  for (const item of [...baseline, ...matched]) {
    if (seen.has(item.title)) continue;
    seen.add(item.title);
    out.push(item);
  }
  return out;
}

/**
 * Build the Clinical layer — a provider-grade ("clinician") view of the same reasoning:
 * contributing factors as weighted clinical inputs, source provenance, and a
 * confidence/uncertainty assessment. This is the clinical-professional explainability layer
 * (P-003 claims 4 / 14 / 19), distinct from the plain-English Summary.
 *
 * It reports the citation COUNT but no longer renders the citation list — that belongs to the
 * Citations tab alone, so there is exactly one destination for sources.
 */
export function buildXaiClinical(promptData, featureName = '') {
  const summary = buildXaiSummary(promptData);
  const evidence = buildXaiEvidence(promptData, featureName);
  const topWeight = summary.factors[0]?.importance || 1;

  // Contributing factors expressed as weighted clinical decision inputs for a provider
  const clinicalInputs = summary.factors.map((f) => ({
    label: f.label,
    detail: f.value,
    weight: f.importance,
    weightPct: Math.round((f.importance / topWeight) * 100),
  }));

  const assessment = clinicalInputs.length
    ? `${clinicalInputs.length} clinical factor(s) informed this recommendation; ${evidence.length} evidence citation(s) applied. Model confidence: ${summary.confidenceLabel}.`
    : 'Insufficient structured clinical context to render a provider-grade assessment.';

  // P-003 claim 5: structured CONFIDENCE INTERVAL — a band (not just a label) that tightens as
  // more corroborating data sources are present.
  const n = summary.contextSourceCount;
  const centerPct = Math.min(95, 40 + n * 6);
  const halfWidth = Math.max(4, Math.round(20 - n * 1.5));
  const confidenceInterval = {
    centerPct: Math.round(centerPct),
    lowerPct: Math.round(Math.max(0, centerPct - halfWidth)),
    upperPct: Math.round(Math.min(100, centerPct + halfWidth)),
  };

  // P-003 claim 5: COUNTERFACTUAL explanations — how the recommendation would change under
  // different inputs (the "what would have to be different" view).
  const counterfactuals = [];
  if (summary.factors[0]) {
    counterfactuals.push({
      condition: `If "${summary.factors[0].label}" were absent`,
      effect: 'the primary constraint driving this recommendation would be lifted.',
    });
  }
  if (n < 6) {
    counterfactuals.push({
      condition: 'With more connected data sources',
      effect: `confidence would tighten above the current ${confidenceInterval.lowerPct}–${confidenceInterval.upperPct}% band.`,
    });
  }
  // The "if matching clinical evidence were available" counterfactual has been removed: with
  // baseline citations in place, evidence is never empty, so the branch was unreachable and
  // would have implied to a reviewer that citations are optional.

  return {
    clinicalInputs,
    // Count only — the Clinical tab no longer re-renders the citation list. Citations live in
    // ONE place (the Citations tab) so a reviewer has a single unambiguous destination, and the
    // two tabs now split cleanly: Citations = external authority, Clinical = this user's data.
    citationCount: evidence.length,
    sourceCount: summary.contextSourceCount,
    confidenceLabel: summary.confidenceLabel,
    confidenceInterval,
    counterfactuals,
    domainValidation: summary.domainValidation,
    assessment,
  };
}
